/**
 * @zendir/ui - Stack Layout Primitives
 *
 * Consistent spacing stacks — the most common layout pattern in operator dashboards.
 * Stack, HStack, and VStack provide directional flex with uniform gaps.
 *
 * @example
 * ```tsx
 * // Vertical stack (default)
 * <Stack spacing="md">
 *   <TelemetryCard />
 *   <LinkBudgetCard />
 *   <SpacecraftCard />
 * </Stack>
 *
 * // Horizontal stack
 * <HStack spacing="sm" align="center">
 *   <StatusIndicator status="normal" />
 *   <Body1>Connected</Body1>
 * </HStack>
 *
 * // Responsive spacing
 * <VStack spacing={['sm', 'md', 'lg']}>
 *   <Content />
 * </VStack>
 * ```
 */

import React, { memo, forwardRef } from "react";
import { useTheme } from "../../theme";
import { useBreakpoint } from "./useBreakpoint";
import { resolveResponsiveSpacing } from "./responsive";
import type { ResponsiveValue } from "./useBreakpoint";
import type { SpacingToken } from "./responsive";

type SpacingInput = SpacingToken | string | number;

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Element type to render */
  as?: React.ElementType;
  /** Stack direction */
  direction?: ResponsiveValue<
    "row" | "column" | "row-reverse" | "column-reverse"
  >;
  /** Space between children (spacing token, px, or CSS string) */
  spacing?: ResponsiveValue<SpacingInput>;
  /** Align items (cross-axis) */
  align?: "flex-start" | "flex-end" | "center" | "baseline" | "stretch";
  /** Justify content (main-axis) */
  justify?:
    | "flex-start"
    | "flex-end"
    | "center"
    | "space-between"
    | "space-around"
    | "space-evenly";
  /** Flex wrap */
  wrap?: "nowrap" | "wrap" | "wrap-reverse";
  /** Full width */
  fullWidth?: boolean;
  /** Full height */
  fullHeight?: boolean;
  /** Divider between items — renders a themed line */
  divider?: boolean;
  /** Children */
  children?: React.ReactNode;
}

export const Stack = memo(
  forwardRef<HTMLDivElement, StackProps>(function Stack(
    {
      as: Component = "div",
      direction = "column",
      spacing = "md",
      align,
      justify,
      wrap,
      fullWidth = false,
      fullHeight = false,
      divider = false,
      style,
      children,
      ...rest
    },
    ref,
  ): React.ReactElement {
    const { tokens } = useTheme();
    const { breakpoint } = useBreakpoint();

    const resolvedSpacing = resolveResponsiveSpacing(
      spacing,
      breakpoint,
      tokens,
    );
    const resolvedDirection = Array.isArray(direction)
      ? (direction[
          Math.min(
            ["base", "sm", "md", "lg", "xl", "xxl"].indexOf(breakpoint),
            direction.length - 1,
          )
        ] ?? direction[direction.length - 1])
      : direction;
    const isVertical =
      resolvedDirection === "column" || resolvedDirection === "column-reverse";

    const computedStyle: React.CSSProperties = {
      display: "flex",
      flexDirection: resolvedDirection as
        | "row"
        | "column"
        | "row-reverse"
        | "column-reverse",
      ...(divider || !resolvedSpacing ? {} : { gap: resolvedSpacing }),
      alignItems: align,
      justifyContent: justify,
      flexWrap: wrap,
      ...(fullWidth ? { width: "100%" } : {}),
      ...(fullHeight ? { height: "100%" } : {}),
      ...style,
    };

    // If divider is enabled, interleave divider elements
    if (divider) {
      const items = React.Children.toArray(children).filter(Boolean);
      const dividerStyle: React.CSSProperties = isVertical
        ? {
            width: "100%",
            height: "1px",
            backgroundColor: tokens.colors.border.muted,
            flexShrink: 0,
            margin: `${resolvedSpacing} 0`,
          }
        : {
            height: "100%",
            width: "1px",
            backgroundColor: tokens.colors.border.muted,
            flexShrink: 0,
            alignSelf: "stretch",
            margin: `0 ${resolvedSpacing}`,
          };

      return (
        <Component ref={ref} style={computedStyle} {...rest}>
          {items.map((child, index) => (
            <React.Fragment key={index}>
              {child}
              {index < items.length - 1 && (
                <div style={dividerStyle} aria-hidden="true" />
              )}
            </React.Fragment>
          ))}
        </Component>
      );
    }

    return (
      <Component ref={ref} style={computedStyle} {...rest}>
        {children}
      </Component>
    );
  }),
);

/**
 * Horizontal Stack — `<Stack direction="row">` shorthand.
 *
 * @example
 * ```tsx
 * <HStack spacing="sm" align="center">
 *   <Icon name="satellite" />
 *   <Body1>SAT-1</Body1>
 *   <Badge status="normal">Nominal</Badge>
 * </HStack>
 * ```
 */
export const HStack = memo(
  forwardRef<HTMLDivElement, Omit<StackProps, "direction">>(
    function HStack(props, ref) {
      return <Stack ref={ref} direction="row" {...props} />;
    },
  ),
);

/**
 * Vertical Stack — `<Stack direction="column">` shorthand.
 *
 * @example
 * ```tsx
 * <VStack spacing="md">
 *   <TelemetryCard />
 *   <LinkBudgetCard />
 * </VStack>
 * ```
 */
export const VStack = memo(
  forwardRef<HTMLDivElement, Omit<StackProps, "direction">>(
    function VStack(props, ref) {
      return <Stack ref={ref} direction="column" {...props} />;
    },
  ),
);

export default Stack;
